import { useState, useEffect } from 'react';
import apiClient from '../lib/api';
import { Loader2, CheckCircle2, AlertCircle, Info, TrendingUp, Clock, MessageSquare, Calendar, Send } from 'lucide-react';
import { formatMessageTime, formatMessageDate } from '../utils/dateHelpers';

interface BusinessInsightsStats {
  time_saved_minutes: number;
  time_saved_hours: number;
  success_rate_percentage: number;
  total_bookings: number;
  total_auto_replies: number;
  total_human_needed: number;
  quota_usage_percentage: number;
  quota_limit: number;
  quota_used: number;
}

interface ActivityLog {
  id: string;
  business_id: number;
  created_at: string;
  event_type: 'BOOKING_SUCCESS' | 'AUTO_REPLY' | 'HUMAN_NEEDED' | 'QUOTA_LIMIT' | 'LEAD_DROPPED';
  description: string;
  severity: 'SUCCESS' | 'INFO' | 'WARNING' | 'ERROR';
  metadata: Record<string, any>;
}

export default function InsightsView() {
  const [stats, setStats] = useState<BusinessInsightsStats | null>(null);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch stats and logs in parallel
      const [statsResponse, logsResponse] = await Promise.all([
        apiClient.get<BusinessInsightsStats>('/api/insights/stats'),
        apiClient.get<{ logs: ActivityLog[]; total_count: number; limit: number; offset: number; has_more: boolean }>('/api/insights/activity'),
      ]);
      
      setStats(statsResponse.data);
      setLogs(logsResponse.data.logs || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'טעינת התובנות נכשלה');
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'SUCCESS':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'INFO':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'WARNING':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'ERROR':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'SUCCESS':
        return 'bg-green-50 border-green-200';
      case 'INFO':
        return 'bg-blue-50 border-blue-200';
      case 'WARNING':
        return 'bg-yellow-50 border-yellow-200';
      case 'ERROR':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const handleSendWhatsApp = (phone: string) => {
    // Open WhatsApp with the phone number
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl w-full space-y-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">תובנות ופעילות</h1>
        <p className="text-sm sm:text-base text-gray-600">צפה בערך שהבוט מביא לעסק שלך</p>
      </div>

      {/* Section A: ROI Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Time Saved Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Clock className="w-8 h-8 text-blue-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">זמן שנחסך</h3>
            <p className="text-2xl font-bold text-gray-900">
              {stats.time_saved_hours.toFixed(1)} שעות
            </p>
            <p className="text-xs text-gray-500 mt-1">החודש האחרון</p>
          </div>

          {/* Success Rate Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <MessageSquare className="w-8 h-8 text-green-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">טיפול אוטומטי</h3>
            <p className="text-2xl font-bold text-gray-900">
              {stats.success_rate_percentage.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">שיעור הצלחה</p>
          </div>

          {/* Bookings Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Calendar className="w-8 h-8 text-purple-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">תורים שנקבעו</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.total_bookings}</p>
            <p className="text-xs text-gray-500 mt-1">החודש האחרון</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Section B: Activity Timeline */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">ציר זמן פעילות</h2>
              <p className="text-sm text-gray-600 mt-1">אירועים אחרונים מהבוט</p>
            </div>
            <div className="p-4 sm:p-6">
              {logs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm">אין פעילות עדיין</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className={`p-4 rounded-lg border ${getSeverityColor(log.severity)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getSeverityIcon(log.severity)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900">{log.description}</p>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {formatMessageTime(log.created_at)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">
                            {formatMessageDate(log.created_at)}
                          </p>
                          {log.event_type === 'HUMAN_NEEDED' && log.metadata?.user_phone && (
                            <button
                              onClick={() => handleSendWhatsApp(log.metadata.user_phone)}
                              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-md hover:bg-green-600 transition-colors"
                            >
                              <Send className="w-3 h-3" />
                              שלח WhatsApp
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section C: Usage & Quota */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">שימוש יומי</h2>
            {stats && (
              <>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">הודעות היום</span>
                    <span className={`text-sm font-medium ${
                      stats.quota_usage_percentage >= 100 
                        ? 'text-red-600' 
                        : stats.quota_usage_percentage >= 80 
                        ? 'text-yellow-600' 
                        : 'text-gray-900'
                    }`}>
                      {stats.quota_used} / {stats.quota_limit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        stats.quota_usage_percentage >= 100
                          ? 'bg-red-500'
                          : stats.quota_usage_percentage >= 80
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(stats.quota_usage_percentage, 100)}%` }}
                    />
                  </div>
                </div>
                {stats.quota_usage_percentage >= 100 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm font-medium text-red-800 mb-1">הבוט עצר!</p>
                    <p className="text-xs text-red-700">הגעת למגבלה היומית. שדרג עכשיו כדי להמשיך.</p>
                  </div>
                )}
                {stats.quota_usage_percentage >= 80 && stats.quota_usage_percentage < 100 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm font-medium text-yellow-800 mb-1">קרוב למגבלה</p>
                    <p className="text-xs text-yellow-700">נשארו {stats.quota_limit - stats.quota_used} הודעות היום.</p>
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">תגובות אוטומטיות:</span>
                      <span className="font-medium text-gray-900">{stats.total_auto_replies}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">נדרש טיפול אנושי:</span>
                      <span className="font-medium text-gray-900">{stats.total_human_needed}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
